import { LegalPage } from './LegalPage'

export function BaaPage() {
  return (
    <LegalPage title="Business Associate Agreement" lastUpdated="June 7, 2026">
      <p>
        This Business Associate Agreement ("BAA") is entered into between TailwindX, LLC ("Business Associate") and the healthcare provider using Dr. Scribey ("Covered Entity"), effective as of the date of account registration.
      </p>

      <h2>1. Definitions</h2>
      <ul>
        <li><strong>"PHI"</strong> means Protected Health Information as defined under HIPAA (45 CFR §160.103).</li>
        <li><strong>"ePHI"</strong> means electronic Protected Health Information.</li>
        <li><strong>"HIPAA Rules"</strong> means the Privacy, Security, and Breach Notification Rules at 45 CFR Parts 160 and 164.</li>
        <li><strong>"Services"</strong> means the Dr. Scribey clinical documentation platform and related features.</li>
      </ul>

      <h2>2. Permitted Uses and Disclosures</h2>
      <p>Business Associate may use or disclose PHI only as necessary to:</p>
      <ul>
        <li>Provide transcription services (audio-to-text conversion)</li>
        <li>Generate clinical documentation (AI-assisted note creation)</li>
        <li>Provide coding suggestions (ICD-10 and CPT code recommendations)</li>
        <li>Perform quality assurance and service improvement</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>
        Business Associate shall not use or disclose PHI for any purpose other than as permitted by this BAA or as required by law.
      </p>

      <h2>3. Safeguards</h2>
      <p>Business Associate shall implement the following safeguards to protect ePHI:</p>

      <h3>Administrative Safeguards</h3>
      <ul>
        <li>Designated security officer responsible for HIPAA compliance</li>
        <li>Workforce training on PHI handling procedures</li>
        <li>Risk assessments conducted regularly</li>
        <li>Incident response and breach notification procedures</li>
      </ul>

      <h3>Technical Safeguards</h3>
      <ul>
        <li><strong>PHI Tokenization:</strong> Patient identifiers are replaced with anonymous tokens before AI model processing</li>
        <li><strong>Encryption in transit:</strong> TLS 1.2+ for all data transmission</li>
        <li><strong>Encryption at rest:</strong> AES-256 encryption on all storage volumes</li>
        <li><strong>Access controls:</strong> Unique user authentication, automatic session timeout (30 minutes)</li>
        <li><strong>Audit controls:</strong> Immutable logging of all access to and modifications of ePHI</li>
        <li><strong>Integrity controls:</strong> Version tracking and edit history for all clinical documents</li>
      </ul>

      <h3>Physical Safeguards</h3>
      <ul>
        <li>Infrastructure hosted on AWS, which maintains SOC 2 Type II, ISO 27001, and HIPAA compliance</li>
        <li>Data center physical security managed by AWS</li>
      </ul>

      <h2>4. Subcontractors</h2>
      <p>
        Business Associate may engage the following subcontractors who may have access to ePHI, and shall ensure each enters into a BAA:
      </p>
      <ul>
        <li><strong>Amazon Web Services:</strong> Cloud infrastructure, AI processing (Bedrock)</li>
        <li><strong>Deepgram:</strong> Speech-to-text transcription</li>
      </ul>
      <p>
        Business Associate shall notify Covered Entity of any changes to subcontractors that handle ePHI.
      </p>

      <h2>5. Breach Notification</h2>
      <ul>
        <li>Business Associate shall report any Breach of Unsecured PHI to Covered Entity without unreasonable delay, and in no case later than <strong>60 calendar days</strong> after discovery of the Breach.</li>
        <li>Notification shall include: identification of affected individuals (if known), description of the Breach, types of PHI involved, steps taken to mitigate harm, and corrective actions.</li>
        <li>Business Associate shall cooperate with Covered Entity's investigation and notification obligations.</li>
      </ul>

      <h2>6. Individual Rights</h2>
      <p>Business Associate shall:</p>
      <ul>
        <li>Make PHI available to Covered Entity for individual access requests within 15 business days</li>
        <li>Make PHI available for amendment and incorporate amendments as directed by Covered Entity</li>
        <li>Document and make available information required for an accounting of disclosures</li>
      </ul>

      <h2>7. Obligations of Covered Entity</h2>
      <ul>
        <li>Covered Entity shall obtain any necessary patient consents or authorizations for use of AI-assisted documentation</li>
        <li>Covered Entity is responsible for reviewing and approving all AI-generated content before clinical use</li>
        <li>Covered Entity shall notify Business Associate of any restrictions on use of PHI that may affect Business Associate's obligations</li>
      </ul>

      <h2>8. Term and Termination</h2>
      <ul>
        <li>This BAA is effective upon account registration and remains in effect for the duration of the Service subscription.</li>
        <li>Upon termination, Business Associate shall return or destroy all PHI within 30 days. If return or destruction is not feasible, protections of this BAA shall continue.</li>
        <li>Either party may terminate this BAA for material breach, with 30 days written notice and opportunity to cure.</li>
      </ul>

      <h2>9. General Provisions</h2>
      <ul>
        <li>This BAA shall be interpreted consistently with HIPAA Rules.</li>
        <li>Any ambiguity shall be resolved in favor of a meaning that complies with HIPAA.</li>
        <li>This BAA supplements and does not replace the Terms of Service.</li>
        <li>Governed by the laws of the State of Wyoming.</li>
      </ul>

      <h2>10. Contact</h2>
      <p>
        For questions about this BAA or to report a security concern, contact:{' '}
        <a href="mailto:info@tailwindx.org" className="text-indigo-600 hover:text-indigo-700">info@tailwindx.org</a>
      </p>

      <p className="mt-8 p-4 bg-indigo-50 rounded-lg text-sm text-gray-600">
        <strong>Acceptance:</strong> By registering for Dr. Scribey and checking the BAA acknowledgment box, you agree to the terms of this Business Associate Agreement on behalf of yourself and/or your practice.
      </p>
    </LegalPage>
  )
}
